/**
 * A2A Client Library - Agent-to-Agent Communication
 * Version: 1.0
 * 
 * A comprehensive client library for A2A protocol communication.
 * Implements all 4 core A2A tasks:
 * 1. Capability Discovery
 * 2. Task Execution & State Management
 * 3. Secure Collaboration
 * 4. UX Negotiation
 * 
 * Based on A2A Protocol v1.0 specification
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Error codes for A2A communication
 */
export const A2AErrorCodes = {
  CAPABILITY_NOT_FOUND: 'CAPABILITY_NOT_FOUND',
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  AUTHORIZATION_DENIED: 'AUTHORIZATION_DENIED',
  INVALID_PARAMETERS: 'INVALID_PARAMETERS',
  TASK_TIMEOUT: 'TASK_TIMEOUT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
};

/**
 * A2A Client for communicating with remote agents
 */
export class A2AClient {
  /**
   * Create a new A2A Client
   * 
   * @param {string} remoteAgentUrl - URL of the remote agent
   * @param {string} agentId - Unique identifier for this client agent
   * @param {Object} options - Configuration options
   * @param {string} options.secretKey - Secret key for message signing
   * @param {number} options.timeout - Request timeout in milliseconds (default: 30000)
   * @param {number} options.maxRetries - Maximum retry attempts (default: 3)
   * @param {number} options.retryDelay - Delay between retries in ms (default: 1000)
   * @param {boolean} options.enableLogging - Enable console logging (default: false)
   */
  constructor(remoteAgentUrl, agentId, options = {}) {
    this.remoteUrl = remoteAgentUrl;
    this.agentId = agentId;
    this.secretKey = options.secretKey || null;
    this.timeout = options.timeout || 30000;
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 1000;
    this.enableLogging = options.enableLogging || false;
    
    // Internal state
    this.capabilities = null;
    this.taskSubscriptions = new Map();
    this.requestCache = new Map();
    this.rateLimitInfo = {
      remaining: null,
      reset: null,
    };
    
    this.log('A2A Client initialized', { remoteUrl: this.remoteUrl, agentId: this.agentId });
  }

  /**
   * Update the remote agent URL dynamically
   * @param {string} newUrl - New remote agent URL
   */
  updateRemoteUrl(newUrl) {
    this.remoteUrl = newUrl;
    this.log('Remote URL updated', { newUrl: this.remoteUrl });
  }

  /**
   * Internal logging method
   */
  log(message, data = null) {
    if (this.enableLogging) {
      console.log(`[A2A Client] ${message}`, data || '');
    }
  }

  /**
   * Internal error logging method
   */
  logError(message, error) {
    if (this.enableLogging) {
      console.error(`[A2A Client] ${message}`, error);
    }
  }

  /**
   * Create a standard A2A message
   * 
   * @param {string} messageType - Type of message (request, response, notification)
   * @param {string} capability - Capability name
   * @param {Object} parameters - Task parameters
   * @param {Object} context - Optional context information
   * @returns {Object} A2A message object
   */
  createMessage(messageType, capability, parameters = {}, context = null) {
    const message = {
      protocol: 'a2a/1.0',
      messageType,
      messageId: uuidv4(),
      timestamp: new Date().toISOString(),
      sender: {
        agentId: this.agentId,
        agentType: 'client',
      },
      receiver: {
        agentId: 'remote-agent',
        agentType: 'remote',
      },
      payload: {
        capability,
        parameters,
      },
    };

    if (context) {
      message.payload.context = context;
    }

    return message;
  }

  /**
   * Sign message with HMAC-SHA256 (Task 3: Secure Collaboration)
   * 
   * @param {Object} message - Message to sign
   * @returns {Promise<string|null>} Signature or null if no secret key
   */
  async signMessage(message) {
    if (!this.secretKey) return null;

    try {
      // Create message string (excluding signature field)
      const messageString = JSON.stringify(message, Object.keys(message).sort());
      
      // Use Web Crypto API for signing
      const encoder = new TextEncoder();
      const keyData = encoder.encode(this.secretKey);
      const messageData = encoder.encode(messageString);

      // Import key
      const key = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );

      // Sign message
      const signature = await crypto.subtle.sign('HMAC', key, messageData);
      
      // Convert to hex string
      return Array.from(new Uint8Array(signature))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    } catch (error) {
      this.logError('Failed to sign message', error);
      return null;
    }
  }

  /**
   * Send HTTP request with retry logic
   * 
   * @param {string} url - Request URL
   * @param {Object} options - Fetch options
   * @param {number} retryCount - Current retry attempt
   * @returns {Promise<Response>} Fetch response
   */
  async sendRequestWithRetry(url, options, retryCount = 0) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check for rate limiting
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter ? parseInt(retryAfter) * 1000 : this.retryDelay * Math.pow(2, retryCount);
        
        if (retryCount < this.maxRetries) {
          this.log(`Rate limited. Retrying after ${delay}ms...`);
          await this.sleep(delay);
          return this.sendRequestWithRetry(url, options, retryCount + 1);
        }
        
        throw new Error(A2AErrorCodes.RATE_LIMIT_EXCEEDED);
      }

      // Retry on server errors
      if (response.status >= 500 && retryCount < this.maxRetries) {
        this.log(`Server error (${response.status}). Retrying...`);
        await this.sleep(this.retryDelay * Math.pow(2, retryCount));
        return this.sendRequestWithRetry(url, options, retryCount + 1);
      }

      return response;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error(A2AErrorCodes.TASK_TIMEOUT);
      }

      if (retryCount < this.maxRetries && this.isRetryableError(error)) {
        this.log(`Network error. Retrying (${retryCount + 1}/${this.maxRetries})...`);
        await this.sleep(this.retryDelay * Math.pow(2, retryCount));
        return this.sendRequestWithRetry(url, options, retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Check if error is retryable
   */
  isRetryableError(error) {
    return error.name === 'TypeError' || // Network errors
           error.message.includes('fetch') ||
           error.message.includes('network');
  }

  /**
   * Sleep utility for retry delays
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Send A2A message to remote agent
   * 
   * @param {Object} message - A2A message
   * @param {string} endpoint - API endpoint (default: '')
   * @returns {Promise<Object>} Response data
   */
  async sendMessage(message, endpoint = '') {
    const url = `${this.remoteUrl}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      'X-Agent-ID': this.agentId,
      'X-Message-ID': message.messageId,
    };

    // Sign message if secret key is available (Task 3: Secure Collaboration)
    const signature = await this.signMessage(message);
    if (signature) {
      headers['X-Agent-Signature'] = signature;
    }

    this.log(`Sending message to ${url}`, { messageId: message.messageId, capability: message.payload.capability });

    try {
      const response = await this.sendRequestWithRetry(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(message),
      });

      // Update rate limit info
      this.rateLimitInfo.remaining = response.headers.get('X-RateLimit-Remaining');
      this.rateLimitInfo.reset = response.headers.get('X-RateLimit-Reset');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      this.log(`Received response`, { messageId: message.messageId });
      
      return data;
    } catch (error) {
      this.logError(`Failed to send message`, error);
      throw new Error(`${A2AErrorCodes.NETWORK_ERROR}: ${error.message}`);
    }
  }

  /**
   * TASK 1: Discover Remote Agent Capabilities
   * 
   * Discovers what capabilities the remote agent has available.
   * This should be called before executing any tasks.
   * 
   * @param {boolean} forceRefresh - Force refresh cached capabilities
   * @returns {Promise<Array>} Array of capability objects
   */
  async discoverCapabilities(forceRefresh = false) {
    // Return cached capabilities if available
    if (this.capabilities && !forceRefresh) {
      this.log('Returning cached capabilities');
      return this.capabilities;
    }

    const message = this.createMessage('request', 'capability-discovery');
    
    try {
      this.log('Discovering capabilities...');
      const response = await this.sendMessage(message, '/a2a/discover');
      
      if (response.payload && response.payload.capabilities) {
        this.capabilities = response.payload.capabilities;
        this.log(`Discovered ${this.capabilities.length} capabilities`);
        return this.capabilities;
      }

      throw new Error(A2AErrorCodes.INVALID_RESPONSE);
    } catch (error) {
      this.logError('Capability discovery failed', error);
      throw error;
    }
  }

  /**
   * Get a specific capability by name
   * 
   * @param {string} capabilityName - Name of the capability
   * @returns {Object|null} Capability object or null if not found
   */
  async getCapability(capabilityName) {
    if (!this.capabilities) {
      await this.discoverCapabilities();
    }

    return this.capabilities.find(cap => cap.name === capabilityName) || null;
  }

  /**
   * Check if a capability exists
   * 
   * @param {string} capabilityName - Name of the capability
   * @returns {Promise<boolean>} True if capability exists
   */
  async hasCapability(capabilityName) {
    const capability = await this.getCapability(capabilityName);
    return capability !== null;
  }

  /**
   * Validate task parameters against capability definition
   * 
   * @param {Object} capability - Capability object
   * @param {Object} params - Task parameters
   * @returns {Object} Validation result { valid: boolean, missing: Array }
   */
  validateParameters(capability, params) {
    const missing = [];
    const provided = Object.keys(params);

    // Check parameters object structure
    if (capability.parameters && typeof capability.parameters === 'object') {
      const requiredParams = Object.keys(capability.parameters);
      for (const param of requiredParams) {
        if (!provided.includes(param)) {
          missing.push(param);
        }
      }
    }

    return {
      valid: missing.length === 0,
      missing,
    };
  }

  /**
   * TASK 2: Execute a task on the remote agent
   * 
   * Executes a capability with given parameters.
   * Automatically discovers capabilities if not already done.
   * Validates parameters before execution.
   * 
   * @param {string} capabilityName - Name of the capability to execute
   * @param {Object} params - Task parameters
   * @param {Object} options - Execution options
   * @param {boolean} options.validateParams - Validate parameters (default: false)
   * @param {number} options.timeout - Override default timeout
   * @returns {Promise<any>} Task result
   */
  async executeTask(capabilityName, params = {}, options = {}) {
    const { validateParams = false, timeout } = options;

    // Ensure capabilities are discovered
    if (!this.capabilities) {
      await this.discoverCapabilities();
    }

    // Find capability
    const capability = this.capabilities.find(cap => cap.name === capabilityName);
    if (!capability && validateParams) {
      throw new Error(`${A2AErrorCodes.CAPABILITY_NOT_FOUND}: '${capabilityName}' not found`);
    }

    // Validate parameters
    if (validateParams && capability) {
      const validation = this.validateParameters(capability, params);
      if (!validation.valid) {
        throw new Error(
          `${A2AErrorCodes.INVALID_PARAMETERS}: Missing required parameters: ${validation.missing.join(', ')}`
        );
      }
    }

    const message = this.createMessage('request', capabilityName, params);

    // Override timeout if specified
    const originalTimeout = this.timeout;
    if (timeout) {
      this.timeout = timeout;
    }

    try {
      this.log(`Executing task: ${capabilityName}`, params);
      const response = await this.sendMessage(message, '/a2a/execute');

      // Check for errors in response
      if (response.payload && response.payload.error) {
        const error = response.payload.error;
        throw new Error(`${error.code}: ${error.message}`);
      }

      // Return result
      if (response.payload && response.payload.result !== undefined) {
        this.log(`Task completed: ${capabilityName}`);
        return response.payload.result;
      }

      throw new Error(A2AErrorCodes.INVALID_RESPONSE);
    } catch (error) {
      this.logError(`Task execution failed: ${capabilityName}`, error);
      throw error;
    } finally {
      // Restore original timeout
      if (timeout) {
        this.timeout = originalTimeout;
      }
    }
  }

  /**
   * TASK 4: Negotiate UX format with remote agent
   * 
   * Sends user context to remote agent and receives optimal UI format recommendations.
   * The remote agent uses AI to determine the best way to present information.
   * 
   * @param {Object} context - User context information
   * @param {string} context.device - Device type (mobile, tablet, desktop)
   * @param {number} context.screen_width - Screen width in pixels
   * @param {number} context.screen_height - Screen height in pixels
   * @param {string} context.bandwidth - Network bandwidth (2g, 3g, 4g, 5g, wifi)
   * @param {string} context.user_level - User expertise level (beginner, intermediate, expert)
   * @param {string} context.preferred_language - Preferred language (traditional, simplified)
   * @returns {Promise<Object>} UX format recommendations
   */
  async negotiateUX(context) {
    const message = {
      protocol: 'a2a/1.0',
      messageType: 'request',
      messageId: uuidv4(),
      timestamp: new Date().toISOString(),
      payload: context,
    };

    try {
      this.log('Negotiating UX format...', context);
      const response = await this.sendMessage(message, '/a2a/negotiate-ux');

      if (response.payload && response.payload.ux_format) {
        this.log('UX format negotiated', response.payload.ux_format);
        return response.payload.ux_format;
      }

      // Return default format on error
      this.log('Using default UX format');
      return this.getDefaultUXFormat(context);
    } catch (error) {
      this.logError('UX negotiation failed, using defaults', error);
      return this.getDefaultUXFormat(context);
    }
  }

  /**
   * Get default UX format based on context
   * 
   * @param {Object} context - User context
   * @returns {Object} Default UX format
   */
  getDefaultUXFormat(context) {
    const isMobile = context.device === 'mobile' || context.screen_width < 768;
    const isSlowNetwork = ['2g', '3g'].includes(context.bandwidth);

    return {
      layout: isMobile ? 'cards' : 'table',
      data_granularity: isSlowNetwork ? 'summary' : 'detailed',
      max_items_per_page: isMobile ? 5 : 10,
      include_help: context.user_level === 'beginner',
      enable_lazy_loading: isSlowNetwork || isMobile,
      font_size: isMobile ? 'large' : 'medium',
      load_images: !isSlowNetwork,
      character_set: context.preferred_language || 'traditional',
      show_pinyin: context.user_level === 'beginner',
      language: (context.preferred_language || 'traditional') === 'traditional' ? 'zh-HK' : 'zh-CN',
    };
  }

  /**
   * Detect user context automatically
   * 
   * @returns {Object} Detected user context
   */
  detectContext() {
    const context = {
      device: this.detectDevice(),
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      bandwidth: this.detectBandwidth(),
      user_level: 'intermediate', // Default, should be set by app
      preferred_language: 'traditional', // Default for Chinese apps
    };

    this.log('Detected context', context);
    return context;
  }

  /**
   * Detect device type
   */
  detectDevice() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Detect network bandwidth
   */
  detectBandwidth() {
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection && connection.effectiveType) {
        return connection.effectiveType;
      }
    }
    return 'wifi'; // Default assumption
  }

  /**
   * Get all available capabilities
   * 
   * @returns {Array|null} Array of capabilities or null if not discovered
   */
  getCapabilities() {
    return this.capabilities;
  }

  /**
   * Get rate limit information
   * 
   * @returns {Object} Rate limit info { remaining, reset }
   */
  getRateLimitInfo() {
    return this.rateLimitInfo;
  }

  /**
   * Clear cached capabilities
   */
  clearCache() {
    this.log('Clearing capabilities cache');
    this.capabilities = null;
    this.requestCache.clear();
  }

  /**
   * Cleanup - unsubscribe from all tasks
   */
  cleanup() {
    this.log('Cleaning up A2A Client');
    this.taskSubscriptions.forEach((intervalId, taskId) => {
      clearInterval(intervalId);
      this.log(`Unsubscribed from task: ${taskId}`);
    });
    this.taskSubscriptions.clear();
  }
}

/**
 * Export default
 */
export default A2AClient;

